import { PropsWithChildren } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { useAuth, useUser } from '@pletly/feature-auth';
import {
	EditOrganisation,
	OrganisationPage,
	NewOrganisationMember,
	AllTermsOfUsePage,
	NewTermsOfUsePage,
	AcceptTermsPage,
	TermsOfUseView,
} from '../screens';
import { CareRecipientCreatedConfirmation } from '../screens/member/new/confirmation';
import { FillCareRecipientInformation } from '../screens/member/new/fillInformation';
import { FillInformationConfirmation } from '../screens/member/new/fillInformationConfirmation';
import { OrganisationsStackParamsList } from './types';
import { NewTeam } from '../screens/team/new';
import { OrganisationTeamPage } from '../screens/team/view';
import { AssignMemberToTeams } from '../screens/member/teams/assign';
import { AddMembersToTeam } from '../screens/team/view/add-members';
import { EditTeam } from '../screens/team/edit';
import { InvitationsFromExternalCaregivers } from '../screens/invitations';
import ImportOrganisationMember from '../screens/member/import';
import { NewTemporaryAccess } from '../screens/member/temporary-access';
import { useOrganisationInfo } from '../context';
import { OrganisationRole } from '../__generated__/types';

const Stack = createNativeStackNavigator<OrganisationsStackParamsList>();

function OrganisationsStack() {
	const user = useUser();
	const { state } = useAuth();
	const { organisationInfo } = useOrganisationInfo();

	if (!organisationInfo) {
		return null;
	}

	return (
		<OrganisationStackContainer>
			<Stack.Screen
				name="Home"
				// @ts-expect-error
				component={state.callbackUrl ? AcceptTermsPage : OrganisationPage}
				initialParams={{ uuid: organisationInfo.uuid }}
			/>
			<Stack.Screen name="AcceptTermsOfUse" component={AcceptTermsPage} />

			{user?.role === 'ORGANISATION_ADMIN' && (
				<>
					<Stack.Screen name="EditOrganisation" component={EditOrganisation} />
					<Stack.Screen name="AllTermsOfUse" component={AllTermsOfUsePage} />
					<Stack.Screen name="NewTermsOfUse" component={NewTermsOfUsePage} />
				</>
			)}

			{(user?.role === 'ORGANISATION_ADMIN' ||
				organisationInfo?.role === OrganisationRole.Manager) && (
				<>
					<Stack.Screen name="OrganisationView" component={OrganisationPage} />
					<Stack.Screen name="TermsOfUseView" component={TermsOfUseView} />
					<Stack.Screen name="NewOrganisationMember" component={NewOrganisationMember} />
					<Stack.Screen name="ImportOrganisationMember" component={ImportOrganisationMember} />
					<Stack.Screen
						name="CareRecipientCreatedConfirmation"
						component={CareRecipientCreatedConfirmation}
					/>
					<Stack.Screen
						name="FillCareRecipientInformation"
						component={FillCareRecipientInformation}
					/>
					<Stack.Screen
						name="FillCareRecipientInformationConfirmation"
						component={FillInformationConfirmation}
					/>
					<Stack.Screen
						name="NewTeam"
						component={NewTeam}
						options={{ presentation: 'transparentModal' }}
					/>
					<Stack.Screen name="EditTeam" component={EditTeam} />
					<Stack.Screen
						name="AssignMemberToTeams"
						component={AssignMemberToTeams}
						options={{ presentation: 'transparentModal' }}
					/>
					<Stack.Screen
						name="AddMembersToTeam"
						component={AddMembersToTeam}
						options={{ presentation: 'transparentModal' }}
					/>
					<Stack.Screen name="Invitations" component={InvitationsFromExternalCaregivers} />
					<Stack.Screen name="NewTemporaryAccess" component={NewTemporaryAccess} />
				</>
			)}
			{organisationInfo?.role && (
				<Stack.Screen name="OrganisationTeam" component={OrganisationTeamPage} />
			)}
		</OrganisationStackContainer>
	);
}

const OrganisationStackContainer = ({ children }: PropsWithChildren) => {
	return (
		<Stack.Navigator
			screenOptions={{
				headerShown: false,
			}}
		>
			{children}
		</Stack.Navigator>
	);
};

export { OrganisationsStack, OrganisationStackContainer };
